import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, hasInboxAccess, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import camelcaseKeys from 'camelcase-keys';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const {
        content_attributes: contentAttributes = {},
        message_type: messageType,
      } = message;
      const { email = {} } = contentAttributes;
      const isIncomingOrOutgoing =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.INCOMING;
      if (email.from && isIncomingOrOutgoing) {
        return true;
      }
      return false;
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    const inboxes = rootGetters['inboxes/getInboxes'];
    const isManager = rootGetters.getCurrentRole === 'manager';

    return _state.allConversations.filter(conversation => {
      if (!hasInboxAccess(conversation, inboxes, { strict: isManager })) {
        return false;
      }
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine;
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _, __, rootGetters) => activeFilters => {
    const inboxes = rootGetters['inboxes/getInboxes'];
    const isManager = rootGetters.getCurrentRole === 'manager';
    return _state.allConversations.filter(conversation => {
      if (!hasInboxAccess(conversation, inboxes, { strict: isManager })) {
        return false;
      }
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return isUnAssigned && shouldFilter;
    });
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const inboxes = rootGetters['inboxes/getInboxes'];
    const isManager = rootGetters.getCurrentRole === 'manager';
    return _state.allConversations.filter(conversation => {
      if (!hasInboxAccess(conversation, inboxes, { strict: isManager })) {
        return false;
      }
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return shouldFilter;
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },
  getComIAChats: (_state, _, __, rootGetters) => activeFilters => {
    const inboxes = rootGetters['inboxes/getInboxes'];
    const isManager = rootGetters.getCurrentRole === 'manager';
    // For Com IA tab, backend handles ALL filtering (AI inboxes + label exclusions)
    // Frontend should just apply basic page filters and return conversations as-is
    // DO NOT apply additional label filtering here - backend handles it
    return _state.allConversations.filter(conversation => {
      if (!hasInboxAccess(conversation, inboxes, { strict: isManager })) {
        return false;
      }
      const shouldFilter = applyPageFilters(conversation, activeFilters);
      return shouldFilter;
    });
  },
  getAtendimentoChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    const currentRole = rootGetters.getCurrentRole;
    const currentAccountId = rootGetters.getCurrentAccountId;
    const featureChecker = rootGetters['accounts/isFeatureEnabledonAccount'];
    const inboxes = rootGetters['inboxes/getInboxes'];
    const isManager = rootGetters.getCurrentRole === 'manager';

    const hideAllChatsForAgentFeatureEnabled =
      currentRole !== 'administrator' &&
      typeof featureChecker === 'function' &&
      currentAccountId &&
      featureChecker(currentAccountId, 'hide_all_chats_for_agent');

    // manager concept: Get manager's teams for visibility check
    const myTeams = currentRole === 'manager' ? (rootGetters['teams/getMyTeams'] || []) : [];
    const myTeamIds = myTeams.map(team => team.id);

    return _state.allConversations.filter(conversation => {
      if (!hasInboxAccess(conversation, inboxes, { strict: isManager })) {
        return false;
      }
      const {
        status: chatStatus,
        labels = [],
        meta: { assignee, team } = {},
      } = conversation;
      const activeLabels = Array.isArray(activeFilters.labels)
        ? activeFilters.labels
        : activeFilters.labels
          ? activeFilters.labels.toString().split(',')
          : [];
      const additionalLabels = activeLabels.filter(
        label => label && label !== 'atendimento-humano'
      );

      // Apply status filtering
      const statusMatches =
        activeFilters.status === 'all' || chatStatus === activeFilters.status;

      if (!statusMatches) {
        return false;
      }

      // Apply label filtering for 'atendimento-humano'
      const hasAtendimentoLabel = labels.includes('atendimento-humano');

      if (!hasAtendimentoLabel) {
        return false;
      }
      if (
        additionalLabels.length &&
        !additionalLabels.every(label => labels.includes(label))
      ) {
        return false;
      }

      if (!hideAllChatsForAgentFeatureEnabled) {
        return true;
      }

      // Check if assigned directly to current user
      if (assignee && assignee.id === currentUserID) {
        return true;
      }

      // manager concept: Managers can also see conversations in their teams
      if (currentRole === 'manager' && team?.id && myTeamIds.includes(team.id)) {
        return true;
      }

      return false;
    });
  },
};

export default getters;
